import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── SUPABASE ───
const SB = "https://jwqyntretasdmwdroryc.supabase.co";
const SK = "sb_publishable_cbVnjyFFQZ7nju94haG3QA_moGN70qR";
const MAX_ROUNDS = 5;

async function sf(path, opts = {}) {
  const r = await fetch(`${SB}/rest/v1/${path}`, {
    headers: { apikey: SK, Authorization: `Bearer ${SK}`, "Content-Type": "application/json", Prefer: opts.prefer || "return=representation" },
    ...opts,
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

const db = {
  questions: {
    // FIXED: Uses 'tags' column and 'cs' (contains) for array logic
    random: (cat, excludeIds = []) => 
      sf(`questions?select=*${cat ? `&tags=cs.{${encodeURIComponent(cat)}}` : ""}`)
        .then(qs => {
          if (!qs || qs.length === 0) return null;
          // Filter out excluded IDs
          const filtered = excludeIds.length > 0 ? qs.filter(q => !excludeIds.includes(q.id)) : qs;
          if (filtered.length === 0) return null; // all seen
          // When no category filter, reduce Proportion weight by 1/3
          if (!cat) {
            const weighted = [];
            for (const q of filtered) {
              const isProportion = Array.isArray(q.tags) && q.tags.some(t => t.toLowerCase() === "proportion");
              weighted.push({ q, w: isProportion ? 2/3 : 1 });
            }
            const total = weighted.reduce((s, e) => s + e.w, 0);
            let r = Math.random() * total;
            for (const e of weighted) { r -= e.w; if (r <= 0) return e.q; }
            return weighted[weighted.length - 1].q;
          }
          return filtered[Math.floor(Math.random() * filtered.length)];
        }),
    
    get: (id) => sf(`questions?id=eq.${id}&select=*`).then(r => r[0]),
  },
  challenges: {
    create: (d) => sf("challenges", { method: "POST", body: JSON.stringify(d) }).then(r => r[0]),
    get: (id) => sf(`challenges?id=eq.${id}&select=*`).then(r => r[0] || null),
    byCode: (c) => sf(`challenges?short_code=eq.${c.toUpperCase()}&select=*`).then(r => r[0] || null),
    update: (id, d) => sf(`challenges?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(d) }).then(r => r[0]),
  },
  rounds: {
    list: (cid) => sf(`rounds?challenge_id=eq.${cid}&select=*,questions(*)&order=round_number.asc`),
    create: (d) => sf("rounds", { method: "POST", body: JSON.stringify(d) }).then(r => r[0]),
    update: (id, d) => sf(`rounds?id=eq.${id}`, { method: "PATCH", body: JSON.stringify(d) }).then(r => r[0]),
  },
};

async function loadFull(input) {
  const c = input.includes("-") ? await db.challenges.get(input) : await db.challenges.byCode(input);
  if (!c) return null;
  const r = await db.rounds.list(c.id);
  return { challenge: c, rounds: r };
}

// ─── IDENTITY ───
function getMe() { try { return localStorage.getItem("dp_name") || ""; } catch { return ""; } }
function setMe(name) { try { localStorage.setItem("dp_name", name); } catch {} }
function clearMe() { try { localStorage.removeItem("dp_name"); } catch {} }

function sanitize(name) { return (name || "").replace(/@/g, "").trim(); }

function amIPlayer(challenge, name) {
  if (!name) return null;
  const n = sanitize(name).toLowerCase();
  if (n === sanitize(challenge.creator_name).toLowerCase()) return "creator";
  if (n === sanitize(challenge.challenger_expected).toLowerCase()) return "challenger";
  return null;
}

// ─── SCORES ───
function calcScores(rounds, challenge) {
  const p1 = challenge.creator_name, p2 = challenge.challenger_expected;
  const s = { [p1]: 0, [p2]: 0 };
  for (const rd of rounds) {
    if (!rd.responder_answer) continue;
    const rN = rd.picker_name === p1 ? p2 : p1;
    if (rd.picker_answer === rd.questions.correct_answer) s[rd.picker_name]++;
    if (rd.responder_answer === rd.questions.correct_answer) s[rN]++;
  }
  return { p1, p2, s1: s[p1], s2: s[p2] };
}

// ─── THEME ───
const T = {
  bg: "#FAF8F5", card: "#F2EFEB", border: "#E8E4DF",
  text: "#2C2C2C", mid: "#6B6560", light: "#A09A93",
  accent: "#C1432E", accentBg: "rgba(193,67,46,0.07)",
  ok: "#3A7D44", okBg: "rgba(58,125,68,0.07)",
  bad: "#C1432E", badBg: "rgba(193,67,46,0.06)",
  white: "#FFFFFF",
  serif: "'Source Serif 4','Georgia',serif",
  sans: "'DM Sans',-apple-system,sans-serif",
  mono: "'JetBrains Mono',monospace",
};
const CATS = ["Proportion", "Assumption", "Flaw", "Analogy"];

// ─── ATOMS ───
const Shell = ({ children, onLogo, me, onClearMe }) => (
  <div style={{ maxWidth: 460, margin: "0 auto", padding: "16px 20px 40px", minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div onClick={onLogo} style={{ cursor: onLogo ? "pointer" : "default", userSelect: "none" }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: T.text, fontFamily: T.sans }}><span style={{ color: T.accent }}>Discourse</span>pass</span>
      </div>
      {me && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, color: T.mid, fontFamily: T.sans }}>{me}</span>
          <button onClick={onClearMe} style={{ background: "none", border: "none", color: T.light, fontSize: 11, cursor: "pointer", fontFamily: T.mono, padding: 2 }} title="Sign out">✕</button>
        </div>
      )}
    </div>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", marginTop: 20 }}>{children}</div>
  </div>
);

const Btn = ({ children, onClick, disabled, primary, sx }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: "100%", padding: 15, borderRadius: 12, fontSize: 15, fontWeight: 600,
    fontFamily: T.sans, cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s", WebkitTapHighlightColor: "transparent",
    ...(primary ? { border: "none", background: disabled ? T.card : T.accent, color: disabled ? T.light : "#fff" }
      : { border: `1.5px solid ${T.border}`, background: "transparent", color: T.mid }),
    ...sx,
  }}>{children}</button>
);

const Pill = ({ children, on, onClick }) => (
  <button onClick={onClick} style={{
    padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, fontFamily: T.sans,
    cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
    border: `1.5px solid ${on ? T.accent : T.border}`,
    background: on ? T.accentBg : "transparent", color: on ? T.accent : T.mid,
    WebkitTapHighlightColor: "transparent",
  }}>{children}</button>
);

const Choice = ({ id, text, selected, correct, revealed, onClick, disabled }) => {
  const ok = revealed && id === correct, bad = revealed && id === selected && id !== correct;
  const sel = !revealed && id === selected, faded = revealed && !ok && !bad;
  let bg = T.white, bdr = T.border, clr = T.text, cBg = T.card, cClr = T.light, cBdr = T.border;
  if (sel) { bdr = T.accent; cBg = T.accent; cClr = "#fff"; cBdr = T.accent; }
  if (ok) { bg = T.okBg; bdr = T.ok; clr = T.ok; cBg = T.ok; cClr = "#fff"; cBdr = T.ok; }
  if (bad) { bg = T.badBg; bdr = T.bad; clr = T.bad; cBg = T.bad; cClr = "#fff"; cBdr = T.bad; }
  return (
    <button onClick={() => !disabled && onClick(id)} disabled={disabled} style={{
      display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "13px 15px",
      borderRadius: 12, background: bg, border: `1.5px solid ${bdr}`, color: clr,
      cursor: disabled ? "default" : "pointer", textAlign: "left", fontSize: 15, lineHeight: 1.5,
      fontFamily: T.sans, fontWeight: 450, transition: "all 0.15s", opacity: faded ? 0.3 : 1,
      WebkitTapHighlightColor: "transparent",
    }}>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 28, height: 28, borderRadius: "50%", fontSize: 12, fontWeight: 700, fontFamily: T.mono, background: cBg, color: cClr, border: `1.5px solid ${cBdr}`, flexShrink: 0 }}>{id}</span>
      <span>{text}</span>
    </button>
  );
};

const Center = ({ emoji, title, sub, children }) => (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "20px 0" }}>
    {emoji && <span style={{ fontSize: 40 }}>{emoji}</span>}
    <p style={{ fontSize: 20, fontWeight: 700, color: T.text, fontFamily: T.sans, marginTop: 10 }}>{title}</p>
    {sub && <div style={{ fontSize: 15, color: T.mid, fontFamily: T.sans, marginTop: 6, maxWidth: 300, lineHeight: 1.5 }}>{sub}</div>}
    {children}
  </div>
);

const CodeBox = ({ code }) => (
  <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 14 }}>
    {code.split("").map((ch, i) => (
      <span key={i} style={{ width: 34, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: T.white, border: `1.5px solid ${T.border}`, fontSize: 17, fontWeight: 700, fontFamily: T.mono, color: T.text }}>{ch}</span>
    ))}
  </div>
);

const Scoreboard = ({ scores, compact }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: compact ? 8 : 12, padding: compact ? "8px 14px" : "12px 18px", borderRadius: 12, background: T.card, border: `1px solid ${T.border}` }}>
    <span style={{ fontSize: compact ? 14 : 16, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{scores.p1}</span>
    <span style={{ fontSize: compact ? 20 : 26, fontWeight: 800, fontFamily: T.mono, color: T.text }}>{scores.s1}</span>
    <span style={{ fontSize: compact ? 12 : 14, color: T.light, fontFamily: T.mono }}>—</span>
    <span style={{ fontSize: compact ? 20 : 26, fontWeight: 800, fontFamily: T.mono, color: T.text }}>{scores.s2}</span>
    <span style={{ fontSize: compact ? 14 : 16, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{scores.p2}</span>
  </div>
);

const PCard = ({ name, correct, answer }) => (
  <div style={{ flex: 1, padding: 12, borderRadius: 12, background: T.white, minWidth: 110, border: `1.5px solid ${correct ? T.ok : T.bad}`, textAlign: "center" }}>
    <p style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{name}</p>
    <p style={{ marginTop: 5, fontSize: 12, fontWeight: 700, fontFamily: T.mono, color: correct ? T.ok : T.bad }}>{correct ? "✓" : "✗"} {answer}</p>
  </div>
);

const RoundTab = ({ num, active, onClick }) => (
  <button onClick={onClick} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: T.mono, cursor: "pointer", border: `1.5px solid ${active ? T.accent : T.border}`, background: active ? T.accentBg : "transparent", color: active ? T.accent : T.light, WebkitTapHighlightColor: "transparent" }}>Q{num}</button>
);

const Spin = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100dvh", background: T.bg }}>
    <div style={{ width: 26, height: 26, borderRadius: "50%", border: `3px solid ${T.card}`, borderTopColor: T.accent, animation: "s .8s linear infinite" }} />
    <style>{`@keyframes s{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function QText({ question }) {
  return <>
    {question.stimulus && <p style={{ fontSize: 16, lineHeight: 1.65, color: T.text, fontFamily: T.serif, marginBottom: 12 }}>{question.stimulus}</p>}
    <p style={{ fontSize: question.stimulus ? 15 : 16, lineHeight: 1.65, color: question.stimulus ? T.mid : T.text, fontFamily: T.sans, fontWeight: question.stimulus ? 600 : 400, marginBottom: 22 }}>{question.stem}</p>
  </>;
}

function copyLink(code) {
  navigator.clipboard.writeText(`${window.location.origin}/api/og?c=${code}`);
}

function shareOrCopy(code, text) {
  const url = `${window.location.origin}/api/og?c=${code}`;
  // Always copy to clipboard first
  navigator.clipboard.writeText(url).catch(() => {});
  // Then open native share on mobile
  const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  if (isMobile && navigator.share) {
    navigator.share({ title: "DiscoursePass", text, url }).catch(() => {});
  }
}

// ─── SCREENS ───

function Home({ onStart, onJoin, onPractice, me, onClearMe }) {
  const [code, setCode] = useState("");
  const [cat, setCat] = useState(null);
  return (
    <div style={{ maxWidth: 460, margin: "0 auto", padding: "16px 20px 40px", minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      {me && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, color: T.mid, fontFamily: T.sans }}>{me}</span>
          <button onClick={onClearMe} style={{ background: "none", border: "none", color: T.light, fontSize: 11, cursor: "pointer", fontFamily: T.mono, padding: 2 }} title="Sign out">✕</button>
        </div>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "20px" }}>
        <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJ0AAABpCAYAAAAgJSHZAAACo0lEQVR4nO3dQVLjMBBAUTM15+EKLDg3C67AhZhVaqhggtWS227rvQ0bMonlH9mJGHtZAAAAAICjPfU8+P31+XPUC6GWl7ePcDuhB4qNm0h8f1ofIDi+ivTQVOnaE/RMs9TU28HmX7x/IrERbWLT4VVwrLnvYOuhtvmcTnB8tcsHCR8caLGll6aZzizHmtYumg+v0Et0pBMd6URXxPvr8+dVPtSJjnSiI53oSCc60omOdH+PfgFsc6XVIDMd6URHOtGRTnRFWJGADqIjnehIJzrSiY50ViSKsCIBHURHOtGRTnRFWJFgWRZXP4hqis4g/3cbC2PSPgahiyLOPtD32z/reERbCB9eZx3oNVf6Dm2rnv3vSpyNjrpW3+15zzDeaVfifPSkZxiIDN50Y/Z96PAavQJjZYIbN9mEz+lmGnDBfdez/cO+p5thtruZMbiR+7cruhkG/yznr2f6qqp3DKZYkYjurLPs5Ku5fHTRlQPncfu5dHTRlQPB7evS0Y0KRXBjdUVX4Zyn9TvFs3xwOLPe/T5spqu0c34atDO/iV7ePp6OHOORzz3Fgv/agG0536v0RsrWs/9D0VU8BD0KT3DbjFr+bP4jzorB3Wx9rZW2KdtaeK3x/Tq4j/7Bqjun4jad6U+blqVvDC34E5K64H/0p6gRfnr91bcrW7SFaW+9eduW+5+0c+vNBoI7xtTRcQxXbSriSrOxmY50oiOd6EgnuiLO9H8keomOdKIjnehIJzrSiY50ViSKsCIBHURHOtGRTnRFWJGADqIjnehIJzrSiY50ViSKsCIBHURHOrfepNvwW29e6VyC/W3pJXS/19jL4Yp2u9/rjDeg43fRC2S63yvN0u/3apbj3u6XCjOz8VWkh66AzHrzMvkAAAAAQG3/AL9oYBna9K1bAAAAAElFTkSuQmCC" alt="Discoursepass" style={{ width: 80, height: "auto" }} />
        <h1 style={{ fontSize: 26, fontWeight: 800, color: T.text, fontFamily: T.sans, letterSpacing: "-0.5px", margin: "10px 0 0" }}><span style={{ color: T.accent }}>Discourse</span>pass</h1>
        <p style={{ marginTop: 8, fontSize: 15, color: T.mid, fontFamily: T.sans, lineHeight: 1.5, maxWidth: 300 }}>Don't fall for the ragebait. Make them prove their critical thinking skills first!</p>
        <div style={{ width: "100%", maxWidth: 320, marginTop: 28 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, justifyContent: "center", marginBottom: 12 }}>
            {CATS.map(c => <Pill key={c} on={cat === c} onClick={() => setCat(cat === c ? null : c)}>{c}</Pill>)}
          </div>
          <Btn primary onClick={() => onStart(cat)}>Create a Challenge</Btn>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", maxWidth: 320, margin: "24px 0" }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          <span style={{ fontSize: 11, color: T.light, fontFamily: T.mono, textTransform: "uppercase", letterSpacing: 1 }}>or join</span>
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>
        <div style={{ display: "flex", gap: 8, width: "100%", maxWidth: 320 }}>
          <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="CODE" maxLength={6}
            style={{ flex: 1, padding: "12px 14px", borderRadius: 10, background: T.white, border: `1.5px solid ${T.border}`, color: T.text, fontSize: 16, fontFamily: T.mono, fontWeight: 600, textAlign: "center", outline: "none", letterSpacing: 3 }}
            onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border}
            onKeyDown={e => e.key === "Enter" && code.trim() && onJoin(code.trim())} />
          <Btn onClick={() => code.trim() && onJoin(code.trim())} sx={{ width: "auto", padding: "12px 20px" }}>Go</Btn>
        </div>
        <button onClick={onPractice} style={{ marginTop: 24, background: "none", border: "none", color: T.light, fontSize: 14, fontFamily: T.sans, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Practice solo</button>
      </div>
    </div>
  );
}

const RefreshIcon = ({ onClick, size = 18 }) => (
  <button onClick={onClick} title="Different question" style={{
    background: "none", border: `1.5px solid ${T.border}`, borderRadius: "50%",
    width: size + 14, height: size + 14, display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: T.light, transition: "all 0.15s", flexShrink: 0,
    WebkitTapHighlightColor: "transparent",
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.light; }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  </button>
);

function QScreen2({ question, onSubmit, header, onLogo, me, onClearMe, onRefresh }) {
  const [sel, setSel] = useState(null);
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      {header && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, padding: "9px 14px", borderRadius: 10, background: T.accentBg, fontSize: 14, color: T.accent, fontWeight: 600, fontFamily: T.sans, textAlign: "center" }}>{header}</div>
          {onRefresh && <RefreshIcon onClick={() => { setSel(null); onRefresh(); }} />}
        </div>
      )}
      {!header && onRefresh && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <RefreshIcon onClick={() => { setSel(null); onRefresh(); }} />
        </div>
      )}
      <QText question={question} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {question.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={sel} onClick={setSel} correct={null} revealed={false} disabled={false} />)}
      </div>
      <div style={{ marginTop: "auto" }}>
        <Btn primary disabled={!sel} onClick={() => sel && onSubmit(sel)}>Lock In</Btn>
      </div>
    </Shell>
  );
}

function NScreen2({ prompt, onSubmit, loading, onLogo, defaultName, me, onClearMe }) {
  const [n, setN] = useState(defaultName || "");
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <p style={{ fontSize: 16, color: T.text, textAlign: "center", maxWidth: 280, fontFamily: T.sans, lineHeight: 1.5 }}>{prompt}</p>
        <input value={n} onChange={e => setN(e.target.value)} placeholder="Username..." maxLength={20} autoFocus disabled={loading}
          style={{ marginTop: 18, width: "100%", maxWidth: 260, padding: "12px 16px", borderRadius: 12, background: T.white, border: `1.5px solid ${T.border}`, color: T.text, fontSize: 16, fontFamily: T.sans, fontWeight: 500, textAlign: "center", outline: "none" }}
          onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = T.border}
          onKeyDown={e => e.key === "Enter" && sanitize(n) && !loading && onSubmit(sanitize(n))} />
        <div style={{ width: "100%", maxWidth: 260, marginTop: 10 }}>
          <Btn primary disabled={!sanitize(n) || loading} onClick={() => sanitize(n) && !loading && onSubmit(sanitize(n))}>{loading ? "..." : "Continue"}</Btn>
        </div>
      </div>
    </Shell>
  );
}

function WaitingForOpponent({ challenge, rounds, onLogo, onHome, onCreateNew, me, onClearMe }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}?c=${challenge.short_code}`;
  const lr = rounds[rounds.length - 1];
  const p1 = challenge.creator_name, p2 = challenge.challenger_expected;
  const waitingOn = lr ? (lr.picker_name === p1 ? p2 : p1) : challenge.challenger_expected;
  const doCopy = () => { copyLink(challenge.short_code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <Center emoji="⏳" title={`Waiting on ${waitingOn}`} sub="They haven't answered yet. Share the link again or challenge someone else!">
        <CodeBox code={challenge.short_code} />
        <div style={{ width: "100%", maxWidth: 280, marginTop: 20 }}>
          <Btn primary onClick={() => { shareOrCopy(challenge.short_code, `${challenge.creator_name} wants you to get a DiscoursePass before continuing the conversation`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? "Copied! ✓" : "Share Again"}
          </Btn>
          <p onClick={doCopy} style={{ marginTop: 8, fontSize: 11, color: T.light, fontFamily: T.mono, cursor: "pointer", wordBreak: "break-all" }}>{link}</p>
        </div>
      </Center>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Btn onClick={onCreateNew}>Challenge Someone Else</Btn>
        <Btn onClick={onHome}>Home</Btn>
      </div>
    </Shell>
  );
}

function LockedIn({ challenge, onLogo, onHome, me, onClearMe }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}?c=${challenge.short_code}`;
  const doCopy = () => { copyLink(challenge.short_code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <Center emoji="🔒" title="Locked in!" sub={<>The answer will be revealed once <strong style={{ color: T.accent }}>{challenge.challenger_expected}</strong> responds.</>}>
        <CodeBox code={challenge.short_code} />
        <div style={{ width: "100%", maxWidth: 280, marginTop: 20 }}>
          <Btn primary onClick={() => { shareOrCopy(challenge.short_code, `${challenge.creator_name} wants you to get a DiscoursePass before continuing the conversation`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? "Copied! ✓" : "Share Challenge"}
          </Btn>
          <p onClick={doCopy} style={{ marginTop: 8, fontSize: 11, color: T.light, fontFamily: T.mono, cursor: "pointer", wordBreak: "break-all" }}>{link}</p>
        </div>
      </Center>
      <Btn onClick={onHome}>Create Another</Btn>
    </Shell>
  );
}

function RoundResult({ round, rounds, challenge, canPickNext, onNext, onHome, onLogo, me, onClearMe }) {
  const [copied, setCopied] = useState(false);
  const q = round.questions;
  const rName = round.picker_name === challenge.creator_name ? challenge.challenger_expected : challenge.creator_name;
  const pOk = round.picker_answer === q.correct_answer;
  const rOk = round.responder_answer === q.correct_answer;
  const scores = calcScores(rounds, challenge);
  const atMax = rounds.filter(r => r.responder_answer).length >= MAX_ROUNDS;
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <Scoreboard scores={scores} />
      <div style={{ display: "flex", gap: 8, marginBottom: 16, marginTop: 16 }}>
        <PCard name={round.picker_name} correct={pOk} answer={round.picker_answer} />
        <PCard name={rName} correct={rOk} answer={round.responder_answer} />
      </div>
      <QText question={q} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
        {q.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={null} correct={q.correct_answer} revealed={true} disabled={true} onClick={() => {}} />)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
        {canPickNext && !atMax && (
          <div style={{ display: "flex", gap: 6 }}>
            <Btn primary onClick={onNext} sx={{ flex: 3 }}>{pOk === rOk ? "Break the Tie — New Round" : "Start another round!"}</Btn>
            <Btn primary onClick={() => { shareOrCopy(challenge.short_code, scores.s1 > scores.s2 ? `${scores.p1} is winning ${scores.s1}–${scores.s2}.` : scores.s2 > scores.s1 ? `${scores.p2} is winning ${scores.s2}–${scores.s1}.` : `It's tied ${scores.s1}–${scores.s2}.`); setCopied(true); setTimeout(() => setCopied(false), 2000); }} sx={{ flex: 1 }}>{copied ? "✓" : "Share"}</Btn>
          </div>
        )}
        {atMax && (
          <div style={{ padding: 14, borderRadius: 12, background: T.card, textAlign: "center", marginBottom: 8 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{scores.s1 > scores.s2 ? `${scores.p1} wins! 🏆` : scores.s2 > scores.s1 ? `${scores.p2} wins! 🏆` : "It's a draw! 🤝"}</p>
            <p style={{ fontSize: 13, color: T.mid, fontFamily: T.sans, marginTop: 4 }}>Final: {scores.s1} — {scores.s2}</p>
          </div>
        )}
        {atMax && (
          <Btn primary onClick={() => { shareOrCopy(challenge.short_code, scores.s1 > scores.s2 ? `${scores.p1} beat ${scores.p2} ${scores.s1}–${scores.s2}!` : scores.s2 > scores.s1 ? `${scores.p2} beat ${scores.p1} ${scores.s2}–${scores.s1}!` : `${scores.p1} and ${scores.p2} tied ${scores.s1}–${scores.s2}!`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? "Copied! ✓" : "Share Result"}</Btn>
        )}
        <Btn onClick={onHome}>Home</Btn>
      </div>
    </Shell>
  );
}

function NotYourTurn({ challenge, question, answer, onChallengeWithAnswer, onPractice, onLogo, me, onClearMe }) {
  const [revealed, setRevealed] = useState(false);
  const isCorrect = answer === question.correct_answer;
  const p1 = challenge.creator_name, p2 = challenge.challenger_expected;
  const lr_picker = challenge.creator_name; // approximate
  const waitingOn = p2;
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <Center emoji="⏳" title={`Waiting on ${waitingOn}`}
        sub="This challenge is meant for someone else — but you can still test yourself!" />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
        <Btn primary onClick={onChallengeWithAnswer}>Send This Question to Someone Else</Btn>
        <Btn onClick={onPractice}>Solo Practice</Btn>
        {!revealed ? (
          <button onClick={() => setRevealed(true)} style={{ background: "none", border: "none", color: T.light, fontSize: 14, fontFamily: T.sans, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: "8px 0" }}>Reveal my answer</button>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>{isCorrect ? "✅" : "❌"}</span>
              <p style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.sans, marginTop: 4 }}>{isCorrect ? "You got it!" : "Not quite."}</p>
            </div>
            <QText question={question} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {question.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={answer} correct={question.correct_answer} revealed={true} disabled={true} onClick={() => {}} />)}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function PickQ({ onSubmit, onLogo, name, me, onClearMe, lockedTag }) {
  const [cat, setCat] = useState(lockedTag || null);
  const [q, setQ] = useState(null);
  const [sel, setSel] = useState(null);
  const [ld, setLd] = useState(true);
  const load = useCallback(async (c) => { setLd(true); setSel(null); try { setQ(await db.questions.random(c)); } catch {} finally { setLd(false); } }, []);
  useEffect(() => { load(lockedTag || null); }, [load, lockedTag]);
  if (ld) return <Spin />;
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <div style={{ padding: "9px 14px", borderRadius: 10, background: T.accentBg, marginBottom: 14, fontSize: 14, color: T.accent, fontWeight: 600, fontFamily: T.sans, textAlign: "center" }}>Your turn, {name}</div>
      {!lockedTag && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
          {CATS.map(c => <Pill key={c} on={cat === c} onClick={() => { setCat(cat === c ? null : c); load(cat === c ? null : c); }}>{c}</Pill>)}
        </div>
      )}
      {lockedTag && (
        <div style={{ marginBottom: 12 }}>
          <span style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: T.sans, background: T.accentBg, color: T.accent, border: `1.5px solid ${T.accent}` }}>{lockedTag}</span>
        </div>
      )}
      {q && <>
        <QText question={q} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {q.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={sel} onClick={setSel} correct={null} revealed={false} disabled={false} />)}
        </div>
      </>}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        <Btn primary disabled={!sel} onClick={() => sel && onSubmit(q, sel)}>Lock In & Send</Btn>
        <Btn onClick={() => load(cat)}>Different Question</Btn>
      </div>
    </Shell>
  );
}

function SpectatorView({ rounds, challenge, onHome, onLogo, onPractice, onChallengeWithAnswer, me, onClearMe }) {
  const [vi, setVi] = useState(0);
  const [ans, setAns] = useState({});
  const [answering, setAnswering] = useState(true);
  const [sel, setSel] = useState(null);
  const cr = rounds.filter(r => r.responder_answer);
  const rd = cr[vi];
  if (!rd) return <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}><Center emoji="⏳" title="No completed rounds yet" /><Btn onClick={onHome}>Home</Btn></Shell>;
  const q = rd.questions, scores = calcScores(cr, challenge);
  const rN = rd.picker_name === challenge.creator_name ? challenge.challenger_expected : challenge.creator_name;
  const pOk = rd.picker_answer === q.correct_answer, rOk = rd.responder_answer === q.correct_answer;
  const myAns = ans[vi], myOk = myAns === q.correct_answer;
  const lock = () => { if (!sel) return; setAns(p => ({ ...p, [vi]: sel })); setAnswering(false); setSel(null); };
  const go = (i) => { setVi(i); setSel(null); setAnswering(!ans[i]); };
  return (
    <Shell onLogo={onLogo} me={me} onClearMe={onClearMe}>
      <Scoreboard scores={scores} compact />
      <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 12, marginBottom: 14 }}>
        {cr.map((_, i) => <RoundTab key={i} num={i + 1} active={vi === i} onClick={() => go(i)} />)}
      </div>
      {answering && !myAns ? <>
        <div style={{ padding: "9px 14px", borderRadius: 10, background: T.accentBg, marginBottom: 14, fontSize: 14, color: T.accent, fontWeight: 600, fontFamily: T.sans, textAlign: "center" }}>Round {vi + 1} — Can you beat them?</div>
        <QText question={q} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {q.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={sel} onClick={setSel} correct={null} revealed={false} disabled={false} />)}
        </div>
        <div style={{ marginTop: "auto" }}><Btn primary disabled={!sel} onClick={lock}>Lock In</Btn></div>
      </> : <>
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
          <PCard name={rd.picker_name} correct={pOk} answer={rd.picker_answer} />
          <PCard name={rN} correct={rOk} answer={rd.responder_answer} />
          {myAns && <PCard name="You" correct={myOk} answer={myAns} />}
        </div>
        <QText question={q} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
          {q.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={myAns} correct={q.correct_answer} revealed={true} disabled={true} onClick={() => {}} />)}
        </div>
        {vi < cr.length - 1 && <Btn primary onClick={() => go(vi + 1)} sx={{ marginBottom: 8 }}>Next Round →</Btn>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
          {myAns && <Btn primary={vi >= cr.length - 1} onClick={() => onChallengeWithAnswer(q, myAns)}>Send This Question to Someone</Btn>}
          <Btn onClick={onPractice}>Solo Practice</Btn>
          <Btn onClick={onHome}>Home</Btn>
        </div>
      </>}
    </Shell>
  );
}

function Practice({ onHome, me, onClearMe }) {
  const [q, setQ] = useState(null);
  const [sel, setSel] = useState(null);
  const [rev, setRev] = useState(false);
  const [sc, setSc] = useState({ ok: 0, n: 0 });
  const [cat, setCat] = useState(null);
  const [ld, setLd] = useState(true);
  const [allSeen, setAllSeen] = useState(false);
  const seenRef = useRef(new Set());

  const load = useCallback(async (c) => {
    setLd(true); setSel(null); setRev(false); setAllSeen(false);
    try {
      const qn = await db.questions.random(c, Array.from(seenRef.current));
      if (!qn) {
        // All questions seen — reset and try again
        seenRef.current = new Set();
        setAllSeen(true);
        const fresh = await db.questions.random(c);
        if (fresh) seenRef.current.add(fresh.id);
        setQ(fresh);
      } else {
        seenRef.current.add(qn.id);
        setQ(qn);
      }
    } catch {} finally { setLd(false); }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const switchCat = (c) => {
    const newCat = cat === c ? null : c;
    setCat(newCat);
    // Reset seen questions when changing category
    seenRef.current = new Set();
    setAllSeen(false);
    load(newCat);
  };

  if (ld) return <Spin />;
  return (
    <Shell onLogo={onHome} me={me} onClearMe={onClearMe}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
        {CATS.map(c => <Pill key={c} on={cat === c} onClick={() => switchCat(c)}>{c}</Pill>)}
      </div>
      <div style={{ padding: "7px 14px", borderRadius: 10, background: T.card, marginBottom: 14, display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontFamily: T.mono, color: T.mid }}>Solo Practice</span>
        <span style={{ fontSize: 14, fontFamily: T.mono, fontWeight: 700, color: T.text }}>{sc.ok}/{sc.n}</span>
      </div>
      {allSeen && (
        <div style={{ padding: "8px 14px", borderRadius: 10, background: T.okBg, marginBottom: 10, fontSize: 13, color: T.ok, fontWeight: 600, fontFamily: T.sans, textAlign: "center" }}>
          You've seen all {cat ? cat : ""} questions! Starting over.
        </div>
      )}
      {q && <>
        <QText question={q} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {q.choices.map(c => <Choice key={c.id} id={c.id} text={c.text} selected={sel} onClick={s => !rev && setSel(s)} correct={q.correct_answer} revealed={rev} disabled={rev} />)}
        </div>
      </>}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        {!rev ? <Btn primary disabled={!sel} onClick={() => { setRev(true); setAllSeen(false); setSc(s => ({ ok: s.ok + (sel === q.correct_answer ? 1 : 0), n: s.n + 1 })); }}>Lock In</Btn>
          : <Btn primary onClick={() => load(cat)}>Next Question</Btn>}
        <Btn onClick={onHome}>Home</Btn>
      </div>
    </Shell>
  );
}

// ─── APP ───
export default function App() {
  const [scr, setScr] = useState("loading");
  const [ch, setCh] = useState(null);
  const [rds, setRds] = useState([]);
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [sav, setSav] = useState(false);
  const [me, setMeState] = useState(getMe());
  const aRef = useRef(null);
  const nRef = useRef(null);
  const createTagRef = useRef(null);

  const updateMe = (name) => { const clean = sanitize(name); setMe(clean); setMeState(clean); };
  const doClearMe = () => { clearMe(); setMeState(""); };

  const home = () => { window.history.pushState({}, "", window.location.pathname); setScr("home"); setCh(null); setRds([]); setQ(null); setErr(null); };

  // Route to correct screen based on identity
  const routeChallenge = (data) => {
    const { challenge: c, rounds: r } = data;
    setCh(c); setRds(r);
    const lr = r[r.length - 1];
    if (!lr) { setScr("not_found"); return; }

    const myRole = amIPlayer(c, me);

    // If latest round is complete (both answered)
    if (lr.responder_answer) {
      if (myRole) {
        // Participant viewing completed round
        setQ(lr.questions);
        setScr("round_result");
      } else {
        setScr("spectator");
      }
      return;
    }

    // Round pending response — figure out who needs to answer
    // The picker already answered. The responder is the other person.
    const p1 = c.creator_name, p2 = c.challenger_expected;
    const responderName = lr.picker_name === p1 ? p2 : p1;
    const meIsResponder = me && sanitize(me).toLowerCase() === sanitize(responderName).toLowerCase();
    const meIsPicker = me && sanitize(me).toLowerCase() === sanitize(lr.picker_name).toLowerCase();

    if (meIsPicker) {
      // I picked this question and I'm waiting for the other person
      setScr("own_link_waiting");
      return;
    }

    if (meIsResponder) {
      // It's my turn to answer
      setQ(lr.questions);
      setScr("resp_answer");
      return;
    }

    // Unknown user or no stored name — let them try answering
    setQ(lr.questions);
    setScr("resp_answer");
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const id = p.get("challenge") || p.get("c");
    if (!id) { setScr("home"); return; }
    loadFull(id).then(d => {
      if (!d) { setScr("not_found"); return; }
      routeChallenge(d);
    }).catch(e => { setErr(e.message); setScr("error"); });
  }, []);

  // ─── CREATE: question → opponent name → your name (skip if stored) → locked ───
  const startCreate = async (cat, qId) => {
    createTagRef.current = cat;
    try { setScr("loading"); const qn = qId ? await db.questions.get(qId) : await db.questions.random(cat); setQ(qn); setScr("c_answer"); }
    catch (e) { setErr(e.message); setScr("error"); }
  };

  const refreshCreateQ = async () => {
    try {
      setScr("loading");
      const qn = await db.questions.random(createTagRef.current);
      setQ(qn);
      setScr("c_answer");
    } catch (e) { setErr(e.message); setScr("error"); }
  };

  const cAnswer = (a) => {
    aRef.current = a;
    setScr("c_opp_name");
  };

  const cOppName = (opp) => {
    nRef.current = opp;
    if (me) {
      // Already have a name — skip to finish
      finishCreate(me);
    } else {
      setScr("c_name");
    }
  };

  const cName = (n) => {
    updateMe(n);
    finishCreate(n);
  };

  // Skip the question — answer already provided, go straight to opponent name (or finish)
  const challengeWithAnswer = (question, answer) => {
    setQ(question);
    aRef.current = answer;
    setScr("c_opp_name");
  };

  const finishCreate = async (creatorName) => {
    try {
      setSav(true);
      const c = await db.challenges.create({ question_id: q.id, creator_name: creatorName, creator_answer: aRef.current, challenger_expected: nRef.current, status: "pending", tag: createTagRef.current || null });
      await db.rounds.create({ challenge_id: c.id, round_number: 1, question_id: q.id, picker_name: creatorName, picker_answer: aRef.current });
      const full = await db.challenges.get(c.id);
      setCh(full); setScr("locked");
    } catch (e) { setErr(e.message); setScr("error"); }
    finally { setSav(false); }
  };

  // ─── RESPOND ───
  const rAnswer = (a) => {
    aRef.current = a;
    if (me) {
      // Auto-check identity
      doRespond(me);
    } else {
      setScr("resp_name");
    }
  };

  const doRespond = async (name) => {
    // The expected responder is whoever didn't pick this round
    const lr = rds[rds.length - 1];
    const p1 = ch.creator_name, p2 = ch.challenger_expected;
    const expectedResponder = lr.picker_name === p1 ? p2 : p1;

    if (sanitize(name).toLowerCase() !== sanitize(expectedResponder).toLowerCase()) {
      // Not the right person
      setScr("not_your_turn");
      return;
    }
    updateMe(name);
    try {
      setSav(true);
      const lr = rds[rds.length - 1];
      if (lr && !lr.responder_answer) await db.rounds.update(lr.id, { responder_answer: aRef.current });
      if (ch.status === "pending") await db.challenges.update(ch.id, { challenger_name: name, challenger_answer: aRef.current, status: "active" });
      const d = await loadFull(ch.id); setCh(d.challenge); setRds(d.rounds);
      setQ(d.rounds[d.rounds.length - 1]?.questions); setScr("round_result");
    } catch (e) { setErr(e.message); setScr("error"); }
    finally { setSav(false); }
  };

  const rName = (name) => { updateMe(name); doRespond(name); };

  // ─── NEXT ROUND ───
  const submitNext = async (qn, ans) => {
    try {
      setSav(true);
      const lr = rds[rds.length - 1];
      const picker = lr.picker_name === ch.creator_name ? ch.challenger_expected : ch.creator_name;
      await db.rounds.create({ challenge_id: ch.id, round_number: rds.length + 1, question_id: qn.id, picker_name: picker, picker_answer: ans });
      const d = await loadFull(ch.id); setCh(d.challenge); setRds(d.rounds); setScr("waiting");
    } catch (e) { setErr(e.message); setScr("error"); }
    finally { setSav(false); }
  };

  const lr = rds[rds.length - 1] || null;

  return (
    <div style={{ minHeight: "100dvh", background: T.bg, color: T.text, fontFamily: T.sans, WebkitFontSmoothing: "antialiased" }}>
      <link href="https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700&family=DM+Sans:wght@400;450;500;600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap" rel="stylesheet" />

      {scr === "loading" && <Spin />}
      {scr === "error" && <Shell onLogo={home} me={me} onClearMe={doClearMe}><Center emoji="⚠️" title="Something went wrong" sub={err}><div style={{ marginTop: 14 }}><Btn onClick={home}>Home</Btn></div></Center></Shell>}

      {scr === "home" && <Home onStart={cat => startCreate(cat)} onJoin={async code => {
        try { setScr("loading"); window.history.pushState({}, "", `?c=${code}`);
          const d = await loadFull(code); if (!d) { setScr("not_found"); return; } routeChallenge(d);
        } catch (e) { setErr(e.message); setScr("error"); }
      }} onPractice={() => setScr("practice")} me={me} onClearMe={doClearMe} />}

      {scr === "practice" && <Practice onHome={home} me={me} onClearMe={doClearMe} />}

      {/* CREATE: question → opponent name → your name (skip if stored) → locked */}
      {scr === "c_answer" && q && <QScreen2 question={q} onSubmit={cAnswer} header="Answer first, then send to your opponent" onLogo={home} me={me} onClearMe={doClearMe} onRefresh={refreshCreateQ} />}
      {scr === "c_opp_name" && <NScreen2 prompt="Who are you challenging?" onSubmit={cOppName} onLogo={home} me={me} onClearMe={doClearMe} />}
      {scr === "c_name" && <NScreen2 prompt="What's your username?" onSubmit={cName} loading={sav} onLogo={home} me={me} onClearMe={doClearMe} />}
      {scr === "locked" && ch && <LockedIn challenge={ch} onLogo={home} onHome={home} me={me} onClearMe={doClearMe} />}

      {/* RESPOND */}
      {scr === "resp_answer" && lr?.questions && <QScreen2 question={lr.questions} onSubmit={rAnswer} header={`${lr.picker_name} challenged you`} onLogo={home} me={me} onClearMe={doClearMe} />}
      {scr === "resp_name" && <NScreen2 prompt="What's your username?" onSubmit={rName} loading={sav} onLogo={home} me={me} onClearMe={doClearMe} />}

      {scr === "not_your_turn" && lr?.questions && <NotYourTurn challenge={ch} question={lr.questions} answer={aRef.current} onChallengeWithAnswer={() => challengeWithAnswer(lr.questions, aRef.current)} onPractice={() => setScr("practice")} onLogo={home} me={me} onClearMe={doClearMe} />}

      {scr === "own_link_waiting" && ch && <WaitingForOpponent challenge={ch} rounds={rds} onLogo={home} onHome={home} onCreateNew={() => startCreate(null)} me={me} onClearMe={doClearMe} />}

      {scr === "round_result" && lr?.questions && lr.responder_answer && (
        <RoundResult round={lr} rounds={rds} challenge={ch} canPickNext={true} onNext={() => setScr("pick_q")} onHome={home} onLogo={home} me={me} onClearMe={doClearMe} />
      )}
      {scr === "pick_q" && <PickQ onSubmit={submitNext} onLogo={home} name={lr ? (lr.picker_name === ch.creator_name ? ch.challenger_expected : ch.creator_name) : ""} me={me} onClearMe={doClearMe} lockedTag={ch?.tag || null} />}

      {scr === "waiting" && <Shell onLogo={home} me={me} onClearMe={doClearMe}><Center emoji="⏳" title="Sent!" sub="Waiting for your opponent.">{ch && <CodeBox code={ch.short_code} />}
        <div style={{ width: "100%", maxWidth: 260, marginTop: 16 }}><Btn primary onClick={() => { shareOrCopy(ch.short_code, `${ch.creator_name} wants you to get a DiscoursePass before continuing the conversation`); }}>Share Link</Btn></div></Center><Btn onClick={home}>Home</Btn></Shell>}

      {scr === "spectator" && ch && <SpectatorView rounds={rds} challenge={ch} onHome={home} onLogo={home} onPractice={() => setScr("practice")} onChallengeWithAnswer={challengeWithAnswer} me={me} onClearMe={doClearMe} />}

      {scr === "not_found" && <Shell onLogo={home} me={me} onClearMe={doClearMe}><Center emoji="🤷" title="Not found" sub="This link might be broken."><div style={{ marginTop: 14 }}><Btn onClick={home}>Home</Btn></div></Center></Shell>}
    </div>
  );
}
